// CTV-682: browsers take the default "Save as PDF" filename from
// document.title, so the page swaps in the material's filename for the
// duration of the print dialog.
//
// Three overlapping mechanisms, because no single one covers every browser:
//
//   1. `printNow()` sets the title synchronously *before* window.print() and
//      restores after it returns. This is the path the on-page button uses and
//      it needs no print events at all, so it works even where they are absent.
//   2. beforeprint/afterprint listeners cover Cmd-P / Ctrl-P, which bypasses
//      our button entirely. Chromium and Firefox fire these.
//   3. A matchMedia('print') listener as a fallback for engines that apply the
//      print media query but don't dispatch the events.
//
// All three are idempotent: setting the title twice is harmless, and every
// restore path returns the ORIGINAL title captured on mount rather than
// whatever happens to be set at the time.
//
// Caveat worth knowing: the filename is only a default. Every browser lets the
// user rename the file in the save dialog, and some may ignore document.title
// entirely. Nothing here can guarantee the name that lands on disk.

import React from 'react';

export interface PrintTitleControls {
  /** Print with the material filename applied, then restore the page title. */
  printNow: () => void;
}

export function usePrintTitle(printTitle: string | null): PrintTitleControls {
  // The page title as it was before we touched anything. Captured once so a
  // restore can never write back a print filename.
  const originalRef = React.useRef<string>(
    typeof document === 'undefined' ? '' : document.title,
  );

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    originalRef.current = document.title;
  }, []);

  React.useEffect(() => {
    if (!printTitle || typeof window === 'undefined') return;

    const apply = () => {
      document.title = printTitle;
    };
    const restore = () => {
      document.title = originalRef.current;
    };

    window.addEventListener('beforeprint', apply);
    window.addEventListener('afterprint', restore);

    // Fallback for engines that switch print media without firing the events.
    const mql = typeof window.matchMedia === 'function' ? window.matchMedia('print') : null;
    const onMediaChange = (e: MediaQueryListEvent) => (e.matches ? apply() : restore());
    // addEventListener on MediaQueryList is not universal; fall back to the
    // deprecated addListener where it isn't available.
    if (mql?.addEventListener) mql.addEventListener('change', onMediaChange);
    else mql?.addListener?.(onMediaChange);

    return () => {
      window.removeEventListener('beforeprint', apply);
      window.removeEventListener('afterprint', restore);
      if (mql?.removeEventListener) mql.removeEventListener('change', onMediaChange);
      else mql?.removeListener?.(onMediaChange);
      restore();
    };
  }, [printTitle]);

  const printNow = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    if (printTitle) document.title = printTitle;
    try {
      window.print();
    } finally {
      // window.print() blocks until the dialog is dismissed in the engines we
      // care about; where it doesn't, the afterprint listener above restores.
      document.title = originalRef.current;
    }
  }, [printTitle]);

  return { printNow };
}
